<div class="market-board" [class.light]="routing.theme === 'light'">
  <header class="board-header">
    <h1 class="board-title">
      Market Radar &mdash;
      {{ showMap ? 'Country Sentiment Map' : 'Sentiment Heatmap' }}
    </h1>
    <p class="board-subtitle" *ngIf="!showMap">
      Each cell is the
      <strong>average sentiment for that month</strong>, over the last 12
      months. Greener is more positive, redder more negative; the arrow marks
      the month-over-month move.
    </p>
    <p class="board-subtitle" *ngIf="showMap">
      Each country is shaded by the
      <strong>average sentiment of its constituents</strong>. Hover a country
      for its score and constituent breakdown; darker grey means no coverage.
    </p>
  </header>

  <div class="board-toolbar">
    <div class="tabs">
      <button
        *ngFor="let h of hierarchies"
        type="button"
        class="tab"
        [class.active]="selectedTab === h.id"
        (click)="selectTab(h.id)"
      >
        {{ tabLabel(h.id) }}
      </button>
    </div>

    <div class="toolbar-right">
      <div
        class="filter-group"
        *ngIf="selectedTab === 'country' && view === 'map'"
      >
        <span class="filter-label">Window</span>
        <div class="segmented">
          <button
            *ngFor="let w of windows"
            type="button"
            class="segmented-item"
            [class.active]="window === w"
            (click)="selectWindow(w)"
          >
            {{ w }}
          </button>
        </div>
      </div>

      <div class="filter-group" *ngIf="selectedTab === 'country'">
        <span class="filter-label">View</span>
        <div class="segmented">
          <button
            type="button"
            class="segmented-item"
            [class.active]="view === 'heatmap'"
            (click)="selectView('heatmap')"
          >
            Heatmap
          </button>
          <button
            type="button"
            class="segmented-item"
            [class.active]="view === 'map'"
            (click)="selectView('map')"
          >
            Map
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="board-search">
    <mat-icon class="board-search-icon">search</mat-icon>
    <input
      type="text"
      class="board-search-input"
      [placeholder]="'Search ' + tabLabel(selectedTab).toLowerCase() + '…'"
      [value]="searchTerm"
      (input)="onSearchChange($any($event.target).value)"
    />
    <button
      *ngIf="searchTerm"
      type="button"
      class="board-search-clear"
      (click)="clearSearch()"
      aria-label="Clear search"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <app-news-market-countries
    *ngIf="showMap"
    [categories]="categories"
    [searchTerm]="searchTerm"
    [sentimentParams]="sentimentParams"
  ></app-news-market-countries>

  <section class="heatmap" *ngIf="showHeatmap">
    <div class="empty" *ngIf="heatmapLoading && !heatmap">
      Loading sentiment heatmap&hellip;
    </div>
    <div
      class="empty"
      *ngIf="!heatmapLoading && (!heatmap || !heatmap.rows.length)"
    >
      No {{ tabLabel(selectedTab).toLowerCase() }} sentiment data available.
    </div>
    <div
      class="empty"
      *ngIf="heatmap && heatmap.rows.length && !visibleHeatmapRows.length"
    >
      No {{ tabLabel(selectedTab).toLowerCase() }} match &ldquo;{{
        searchTerm
      }}&rdquo;.
    </div>

    <div class="hm-scroll" *ngIf="heatmap && visibleHeatmapRows.length">
      <table class="hm-matrix">
        <thead>
          <tr>
            <th scope="col" class="hm-corner">{{ heatmapEntityHeader }}</th>
            <th
              scope="col"
              *ngFor="let mo of heatmap.months; trackBy: trackByMonth"
              [class.th-current]="mo.isCurrent"
            >
              {{ mo.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="hm-summary-row">
            <th scope="row" class="hm-summary-label">{{ heatmapAvgLabel }}</th>
            <td *ngFor="let cell of heatmap.avg; let i = index">
              <div
                class="hm-cell hm-avg"
                [class.hm-current]="heatmap.months[i].isCurrent"
                [class.hm-empty]="cell.pct === null"
                [class.hm-cell-clickable]="cellClickable(cell)"
                [style.background]="cellColor(cell)?.bg"
                [style.color]="cellColor(cell)?.fg"
                [attr.role]="cellClickable(cell) ? 'button' : null"
                [attr.tabindex]="cellClickable(cell) ? 0 : null"
                (click)="openAvgPanel(i)"
                (keydown.enter)="openAvgPanel(i)"
              >
                {{ cellLabel(cell)
                }}<span class="hm-delta">{{ cellArrow(cell) }}</span>
              </div>
            </td>
          </tr>

          <ng-container
            *ngFor="let row of visibleHeatmapRows; trackBy: trackByHeatmapRow"
          >
            <tr [class.hm-row-expanded]="isRowExpanded(row)">
              <th scope="row" class="hm-entity">
                <button
                  *ngIf="hasSubCategories(row)"
                  type="button"
                  class="hm-expander"
                  [attr.aria-expanded]="isRowExpanded(row)"
                  [attr.aria-label]="
                    (isRowExpanded(row) ? 'Collapse ' : 'Expand ') + row.name
                  "
                  (click)="toggleRow(row)"
                >
                  <mat-icon>{{
                    isRowExpanded(row) ? 'expand_more' : 'chevron_right'
                  }}</mat-icon>
                </button>
                <span
                  class="hm-entity-name"
                  [class.hm-entity-indent]="!hasSubCategories(row)"
                  >{{ row.name }}</span
                >
              </th>
              <td *ngFor="let cell of row.cells; let i = index">
                <div
                  class="hm-cell"
                  tabindex="0"
                  role="img"
                  [class.hm-current]="heatmap.months[i].isCurrent"
                  [class.hm-empty]="cell.pct === null"
                  [class.hm-cell-clickable]="cellClickable(cell)"
                  [style.background]="cellColor(cell)?.bg"
                  [style.color]="cellColor(cell)?.fg"
                  [attr.aria-label]="
                    cellAria(row.name, heatmap.months[i], cell)
                  "
                  [attr.title]="cellAria(row.name, heatmap.months[i], cell)"
                  (click)="openCellPanel(row, i)"
                  (keydown.enter)="openCellPanel(row, i)"
                >
                  {{ cellLabel(cell)
                  }}<span class="hm-delta">{{ cellArrow(cell) }}</span>
                </div>
              </td>
            </tr>

            <ng-container *ngIf="isRowExpanded(row)">
              <tr
                class="hm-subrow"
                *ngFor="let sub of subRowsOf(row); trackBy: trackByHeatmapRow"
              >
                <th scope="row" class="hm-entity hm-subentity">
                  {{ sub.name }}
                </th>
                <td *ngFor="let cell of sub.cells; let i = index">
                  <div
                    class="hm-cell"
                    tabindex="0"
                    role="img"
                    [class.hm-current]="heatmap.months[i].isCurrent"
                    [class.hm-empty]="cell.pct === null"
                    [class.hm-cell-clickable]="cellClickable(cell)"
                    [style.background]="cellColor(cell)?.bg"
                    [style.color]="cellColor(cell)?.fg"
                    [attr.aria-label]="
                      cellAria(sub.name, heatmap.months[i], cell)
                    "
                    [attr.title]="cellAria(sub.name, heatmap.months[i], cell)"
                    (click)="openCellPanel(sub, i)"
                    (keydown.enter)="openCellPanel(sub, i)"
                  >
                    {{ cellLabel(cell)
                    }}<span class="hm-delta">{{ cellArrow(cell) }}</span>
                  </div>
                </td>
              </tr>
              <tr class="hm-subrow hm-submeta" *ngIf="!subRowsOf(row).length">
                <td [attr.colspan]="heatmap.months.length + 1">
                  No sub-category data available.
                </td>
              </tr>
            </ng-container>
          </ng-container>
        </tbody>
      </table>
    </div>
  </section>

  <app-market-detail-panel
    [entity]="panelEntity"
    [timeframe]="panelTimeframe"
    [timeframeLabel]="panelTimeframeLabel"
    [sentimentParams]="panelSentimentParams"
    [aggregate]="panelAggregate"
    (closed)="closeCellPanel()"
  ></app-market-detail-panel>
</div>
